import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, Coffee } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import OptimizedImage from './OptimizedImage';
import { trackDiscovery } from '../utils/analytics';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 150;
const MAX_SUGGESTIONS = 8;

/**
 * Rank menu items against a normalized query.
 * Order: exact name → starts-with name → contains name → sub_category/category contains.
 * Bestsellers get a small tiebreak bump; sold-out items drop one tier but stay visible.
 */
function rankItems(items, query) {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const scored = [];
  for (const item of items) {
    if (!item || !item.name) continue;
    const name = item.name.toLowerCase();
    // App-only categories (Business Rule #62, e.g. "Signature Drinks") match like the own category.
    const cat = [item.category, ...Object.keys(item.also_in || {})].filter(Boolean).join(' ').toLowerCase();
    const sub = (item.sub_category || '').toLowerCase();
    let score;
    if (name === q) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (name.includes(q)) score = 2;
    else if (sub.includes(q) || cat.includes(q)) score = 3;
    else continue;
    if (Array.isArray(item.tags) && item.tags.includes('Bestseller')) score -= 0.25;
    if (item.available === false) score += 1;
    scored.push({ item, score });
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, MAX_SUGGESTIONS).map(s => s.item);
}

/**
 * Bolds the matched substring within the displayed text (case-insensitive).
 * Returns plain text when the query is empty or no match is found.
 */
function HighlightMatch({ text, query }) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-bold text-foreground">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

/**
 * Live menu-item search dropdown. Triggers at 3 characters with 150ms debounce.
 * Controlled via `query` + `onQueryChange` so the host page can keep its own
 * body-level filtered view in sync (used by Menu.js — Landing only needs the
 * dropdown). Tapping a suggestion navigates to /menu/item/:id; Enter with no
 * row highlighted falls through to `onEnterFullSearch(query)` so the existing
 * full-search flow on Landing is preserved.
 */
export default function MenuSearchAutocomplete({
  query,
  onQueryChange,
  items,
  onEnterFullSearch,
  onClear,
  placeholder = 'Search menu...',
  inputClassName = '',
  autoFocus = false,
  testId = 'menu-search',
}) {
  const navigate = useNavigate();
  // Captured to pass as `backgroundLocation` when opening ProductDetail — keeps
  // the host page (Landing or Menu) mounted under the slide-up sheet. See
  // App.js routing block for the background-location pattern.
  const location = useLocation();
  const [debouncedQuery, setDebouncedQuery] = useState(query || '');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query || ''), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [debouncedQuery]);

  const suggestions = useMemo(() => {
    if ((debouncedQuery || '').trim().length < MIN_QUERY_LENGTH) return [];
    return rankItems(items || [], debouncedQuery);
  }, [items, debouncedQuery]);

  const shouldShow = open && (query || '').trim().length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const handleSelect = useCallback((item) => {
    setOpen(false);
    onQueryChange?.('');
    setDebouncedQuery('');
    // Origination telemetry — which host page's search bar found this item
    trackDiscovery(location.pathname.startsWith('/menu') ? 'menu_search_suggestion' : 'home_search_suggestion', item.name);
    navigate(`/menu/item/${item.id}`, { state: { backgroundLocation: location } });
  }, [navigate, onQueryChange, location]);

  const handleClear = () => {
    onQueryChange?.('');
    setDebouncedQuery('');
    onClear?.();
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!shouldShow) {
      if (e.key === 'Enter' && (query || '').trim()) {
        e.preventDefault();
        onEnterFullSearch?.(query.trim());
        setOpen(false);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(-1, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        handleSelect(suggestions[highlightedIndex]);
      } else if ((query || '').trim()) {
        onEnterFullSearch?.(query.trim());
        setOpen(false);
      }
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query || ''}
          onChange={(e) => { onQueryChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={inputClassName || 'w-full pl-10 pr-9 py-2.5 rounded-full bg-white text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent'}
          autoComplete="off"
          spellCheck="false"
          enterKeyHint="search"
          data-testid={`${testId}-input`}
        />
        {(query || '').length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {shouldShow && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute left-0 right-0 top-full mt-2 z-[60] rounded-2xl border border-border bg-card shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto"
            data-testid={`${testId}-dropdown`}
          >
            {suggestions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                <Coffee className="w-6 h-6 mx-auto mb-2 opacity-40" />
                <p>No items match "{debouncedQuery}"</p>
                <button
                  type="button"
                  onClick={() => { onEnterFullSearch?.(debouncedQuery); setOpen(false); }}
                  className="mt-3 text-xs text-accent font-medium hover:underline"
                  data-testid={`${testId}-empty-cta`}
                >
                  See full menu →
                </button>
              </div>
            ) : (
              <ul className="py-1" role="listbox">
                {suggestions.map((item, idx) => {
                  const isHighlighted = idx === highlightedIndex;
                  const isSoldOut = item.available === false;
                  const price = item.price ?? 0;
                  return (
                    <li key={item.id} role="option" aria-selected={isHighlighted}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors min-h-[56px] ${isHighlighted ? 'bg-muted' : 'hover:bg-muted/60'}`}
                        data-testid={`${testId}-row-${idx}`}
                      >
                        <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                          <OptimizedImage
                            src={item.image_url}
                            imageUrls={item.image_urls}
                            size="thumb"
                            alt={item.name}
                            className="w-full h-full object-cover"
                            width={48}
                            height={48}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            <HighlightMatch text={item.name} query={debouncedQuery} />
                          </p>
                          {(item.sub_category || item.category) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {item.sub_category || item.category}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                          <span className="text-sm font-semibold text-foreground">₹{Math.round(price)}</span>
                          {isSoldOut && (
                            <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                              Sold out
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
